import React, { useState, useEffect } from "react";
import { auth, db, checkIsAdmin } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

// Telefon numarası formatı için yardımcı fonksiyon
const formatPhoneNumber = (value) => {
  // Sadece rakamları al
  const numbers = value.replace(/\D/g, "");

  // Maksimum 10 rakam
  if (numbers.length > 10) {
    // Eğer 10 rakamdan fazlaysa, ilk 10 rakamı al ve formatla
    const truncated = numbers.slice(0, 10);
    return `(${truncated.slice(0, 3)}) ${truncated.slice(
      3,
      6
    )}-${truncated.slice(6)}`;
  }

  // Format: (XXX) XXX-XXXX
  let formattedNumber = "";
  if (numbers.length >= 1) {
    formattedNumber += "(" + numbers.slice(0, 3);
    if (numbers.length >= 4) {
      formattedNumber += ") " + numbers.slice(3, 6);
      if (numbers.length >= 7) {
        formattedNumber += "-" + numbers.slice(6, 10);
      }
    }
  }
  return formattedNumber;
};

// Telefon numarası validasyonu için fonksiyon
const isValidPhoneNumber = (phone) => {
  // (XXX) XXX-XXXX formatında olmalı
  const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
  return phoneRegex.test(phone);
};

const RegisterForm = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);
  const [formData, setFormData] = useState({
    companyName: "",
    fullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    businessAddress: "",
    businessType: "employer",
    additionalInfo: "",
    facebookUrl: "",
    instagramUrl: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedPhone,
      }));

      // Validasyon kontrolü
      if (formattedPhone.length === 14 && !isValidPhoneNumber(formattedPhone)) {
        setValidationErrors((prev) => ({
          ...prev,
          phone: "Please enter a valid phone number in format: (XXX) XXX-XXXX",
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.phone;
          return newErrors;
        });
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Şifre kontrolü
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match!");
      setLoading(false);
      return;
    }

    // Şifre uzunluk kontrolü
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long!");
      setLoading(false);
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "registrationRequests"), {
        ...formData,
        password: formData.password,
        createdAt: serverTimestamp(),
        status: "pending",
      });
      console.log("Registration request added successfully:", docRef.id);

      setSuccess(true);
      setFormData({
        companyName: "",
        fullName: "",
        phone: "",
        email: "",
        password: "",
        confirmPassword: "",
        businessAddress: "",
        businessType: "employer",
        additionalInfo: "",
        facebookUrl: "",
        instagramUrl: "",
      });

      setTimeout(() => {
        navigate("/");
      }, 5000);
    } catch (err) {
      console.error("Registration error:", err);
      setError(
        "An error occurred while submitting your request. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4'>
      <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8'>
        <h2 className='text-3xl font-semibold text-center mb-8 text-gray-800'>
          Business Registration Request
        </h2>

        <div>
          {success ? (
            <div className='text-center space-y-4'>
              <div className='text-green-600 font-medium text-lg'>
                Request submitted successfully!
              </div>
              <p className='text-gray-600 font-medium text-base'>
                Thank you for your registration request. We will review your
                information and contact you via email with your account details
                once approved.
              </p>
              <p className='text-gray-600 font-medium text-base'>
                Redirecting to home page...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className='space-y-6'>
              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Business Type
                </label>
                <select
                  name='businessType'
                  value={formData.businessType}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required>
                  <option value='employer'>Employer</option>
                  <option value='housing'>Housing Provider</option>
                </select>
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Company/Business Name
                </label>
                <input
                  type='text'
                  name='companyName'
                  value={formData.companyName}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Contact Person Full Name
                </label>
                <input
                  type='text'
                  name='fullName'
                  value={formData.fullName}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Phone Number
                </label>
                <input
                  type='tel'
                  name='phone'
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder='(XXX) XXX-XXXX'
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    validationErrors.phone
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {validationErrors.phone && (
                  <p className='text-red-500 text-sm mt-1'>
                    {validationErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Email Address
                </label>
                <input
                  type='email'
                  name='email'
                  value={formData.email}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Password
                </label>
                <input
                  type='password'
                  name='password'
                  value={formData.password}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  placeholder='At least 6 characters'
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Confirm Password
                </label>
                <input
                  type='password'
                  name='confirmPassword'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  placeholder='Confirm your password'
                  required
                />
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Business Address
                </label>
                <textarea
                  name='businessAddress'
                  value={formData.businessAddress}
                  onChange={handleChange}
                  rows={3}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>

              <div className='space-y-4'>
                <h3 className='text-gray-700 font-medium text-base mb-2'>
                  Social Media (Optional)
                </h3>

                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Facebook Profile
                  </label>
                  <input
                    type='url'
                    name='facebookUrl'
                    value={formData.facebookUrl}
                    onChange={handleChange}
                    placeholder='https://facebook.com/your.business'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  />
                </div>

                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Instagram Profile
                  </label>
                  <input
                    type='url'
                    name='instagramUrl'
                    value={formData.instagramUrl}
                    onChange={handleChange}
                    placeholder='https://instagram.com/your.business'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  />
                </div>
              </div>

              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Additional Information
                </label>
                <textarea
                  name='additionalInfo'
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={4}
                  placeholder='Please provide any additional information about your business, services, or requirements.'
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                />
              </div>

              {error && (
                <p className='text-red-500 text-base font-medium'>{error}</p>
              )}

              <button
                type='submit'
                disabled={loading}
                className='w-full bg-blue-600 text-white py-3 rounded-lg font-medium text-lg hover:bg-blue-700 transition-colors disabled:opacity-50'>
                {loading ? "Submitting..." : "Submit Request"}
              </button>

              <div className='text-center mt-8'>
                <Link
                  to='/login'
                  className='text-blue-600 hover:text-blue-700 font-semibold text-lg hover:underline'>
                  Do you have a business account? Login
                </Link>
              </div>

              <p className='text-gray-600 text-base font-medium text-center mt-6'>
                By submitting this form, you are requesting a business account.
                We will review your information and contact you via email or
                phone number.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
